<template>
  <div class="dnd-grid-container" :style="style">
    <!-- eslint-disable -->
    <slot></slot>
    <box box_id="::placeholder::" class="dnd-grid-box--placeholder" />
  </div>
</template>

<style>
  .dnd-grid-container {
    position: relative;
    transition: min-width ease-out 0.1s, min-height ease-out 0.1s;
  }

  .dnd-grid-container .dnd-grid-box.placeholder {
    opacity: 1;
    z-index: 0;
  }

  .dnd-grid-container .dnd-grid-box.placeholder {
    transition: none !important;
  }
</style>

<script>
  /* eslint-disable */

  import Box from './Box';
  import * as utils from './utils';
  import cloneDeep from 'lodash-es/cloneDeep';

  export const List = new Set();

  export default {
    name: 'DndGridContainer',
    components: {
      Box
    },
    props: {
      layout: {
        type: Array,
        required: true
      },
      maxColumnCount: {
        type: Number,
        default: Infinity
      },
      maxRowCount: {
        type: Number,
        default: Infinity
      },
      autoAddLayoutForNewBox: {
        type: Boolean,
        required: false,
        default: true
      },
      fixLayoutOnLoad: {
        type: Boolean,
        required: false,
        default: true
      }
    },
    data() {
      return {
        placeholder: {
          hidden: true,
          position: {
            x: 0,
            y: 0,
            w: 1,
            h: 1
          }
        },
        dragging: {
          boxLayout: null,
          offset: {
            x: 0,
            y: 0
          },
          destination: {
            x: 0,
            y: 0
          }
        },
        isMounted: false
      };
    },

    computed: {
      style() {
        var layoutSize = utils.getLayoutSize(this.layout);
        return {
          minWidth: `${layoutSize.w}px`,
          minHeight: `${layoutSize.h}px`
        };
      }
    },
    created() {
      List.add(this);
    },
    mounted() {
      this.isMounted = true;
      let boxIds = this.$children.map(box => box.$props.box_id);
      this.createBoxLayout(...boxIds);
    },

    beforeDestroy() {
      List.delete(this);
    },

    methods: {
      getBoxLayoutById(id) {
        if (id === '::placeholder::') {
          return this.placeholder;
        }
        return this.layout.find(f => f.id === id);
      },
      getPixelPositionById(id) {
        if (this.dragging.boxLayout && this.dragging.boxLayout.id === id) {
          let pixels = utils.positionToPixels(this.dragging.boxLayout.position);
          pixels.x += this.dragging.offset.x;
          pixels.y += this.dragging.offset.y;
          return pixels;
        }

        var boxLayout = this.getBoxLayoutById(id);
        return utils.positionToPixels(boxLayout.position);
      },
      isBoxVisible(id) {
        var boxLayout = this.getBoxLayoutById(id);
        return boxLayout && !boxLayout.hidden;
      },
      getPositionByPixel(x, y) {
        return {
          x: Math.ceil(x),
          y: Math.ceil(y)
        };
      },
      updateLayout(layout) {
        this.$emit('update:layout', layout);
      },
      registerBox(box) {
        this.enableDragging(box);
        if (this.isMounted && this.autoAddLayoutForNewBox) {
          this.createBoxLayout(box.$props.box_id);
        }
      },
      unregisterBox(box) {},
      enableDragging(box) {
        var initialLayout;
        var isDragging = false;

        let validateTargetPosition = (targetX, targetY) => {
          if (targetX + this.dragging.boxLayout.position.w > this.maxColumnCount) {
            targetX = this.maxColumnCount - this.dragging.boxLayout.position.w;
          } else {
            if (targetX < 0) {
              targetX = 0;
            }
          }
          if (targetY + this.dragging.boxLayout.position.h > this.maxRowCount) {
            targetY = this.maxRowCount - this.dragging.boxLayout.position.h;
          } else {
            if (targetY < 0) {
              targetY = 0;
            }
          }
          return {
            targetX,
            targetY
          };
        };

        box.$on('on-drag-start', evt => {
          var boxLayout = this.getBoxLayoutById(box.box_id);
          if (boxLayout.pinned) {
            return;
          }
          isDragging = true;

          // find box
          this.dragging.boxLayout = boxLayout;
          this.placeholder = {
            ...this.dragging.boxLayout
          };

          // clone layout
          initialLayout = utils.sortLayout(this.layout);

          this.$emit('drag:start', initialLayout);
          this.updateLayout(initialLayout);
        });

        box.$on('on-drag-update', evt => {
          if (!isDragging) {
            return;
          }

          this.dragging.offset.x = evt.offset.x;
          this.dragging.offset.y = evt.offset.y;

          var moveBy = this.getPositionByPixel(evt.offset.x, evt.offset.y);
          if (
            !utils.isFree([], {
              ...this.dragging.boxLayout.position,
              x: this.dragging.boxLayout.position.x + moveBy.x,
              y: this.dragging.boxLayout.position.y + moveBy.y
            })
          ) {
            return;
          }

          let { targetX, targetY } = validateTargetPosition(
            this.dragging.boxLayout.position.x + moveBy.x,
            this.dragging.boxLayout.position.y + moveBy.y
          );

          // check if box has moved
          if (this.placeholder.position.x === targetX && this.placeholder.position.y === targetY) {
            return;
          }

          this.placeholder = utils.updateBoxPosition(
            this.placeholder,
            this.getPositionByPixel(targetX, targetY)
          );

          const dragBox = cloneDeep({ ...this.placeholder, priority: 0 });
          let l = cloneDeep(this.layout.map(i => ({ ...i, priority: 1000 })));
          this.resolveConflicts(dragBox, l, initialLayout);

          this.updateLayout(l);
          this.$emit('drag:update', l);
        });

        box.$on('on-drag-end', evt => {
          if (!isDragging) {
            return;
          }

          this.dragging.boxLayout = null;
          this.dragging.offset.x = 0;
          this.dragging.offset.y = 0;

          this.placeholder.hidden = true;
          isDragging = false;

          this.$emit('drag:end', cloneDeep(this.layout.map(i => ({ ...i, priority: 1000 }))));
        });
      },
      resolveConflicts(dragBox, layout = [], initialLayout = [], priority = 0) {
        const max_priority = 1000;
        var safe = 100;
        var radius = 32;
        let oldLayout = cloneDeep(initialLayout);
        let layoutWithoutDrag = cloneDeep(oldLayout.filter(i => i.id !== dragBox.id));

        layout.forEach((node, index) => {
          if (node.id === dragBox.id) {
            node = dragBox;
            layout[index] = node;
          }

          if (node.priority > priority) {
            const initNode = initialLayout.find(init => init.id === node.id);
            const initial_x = initNode.position.x;
            const initial_y = initNode.position.y;

            const dx = initial_x - dragBox.position.x;
            const dy = initial_y - dragBox.position.y;
            const angle = Math.atan2(dx, dy);
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < safe) {
              const d = safe - distance;
              const x = initial_x + Math.sin(angle) * d;
              const y = initial_y + Math.cos(angle) * d;

              node.priority = priority;

              node = utils.updateBoxPosition(node, this.getPositionByPixel(x, y));

              layout[index] = node;
              this.resolveConflicts(node, layout, initialLayout, priority + 1);
            } else {
              node.priority = max_priority;
              layout[index] = node;
            }
          }
        });

        layout.map(item => {
          const oldItem = initialLayout.find(f => f.id === item.id);
          return oldItem && utils.isCircleFree(layout, oldItem.position) ? oldItem : item;
        });
      },
      createBoxLayout(...boxIds) {
        boxIds
          .filter(i => i !== '::placeholder::')
          .forEach(id => {
            const box = this.getBoxLayoutById(id);
            let l = cloneDeep(this.layout.map(i => ({ ...i, priority: 1000 })));
            this.resolveConflicts({ ...box, priority: 0 }, l, l);
            this.updateLayout(l);
          });
      }
    }
  };
</script>
